// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { OptionItem } from 'components/molecules/SelectFieldMultipleChoise';
import { createAxios } from 'ts/createAxios';
import { CollectRoute } from 'types/routeManagementTypes';
import { ScalesModelDetail, Station, TenantBase } from 'util/Types';
import { API_ENDPOINTS } from 'util/endPoints';
import { handleError } from 'util/errorHandler';
import { EdiCategoryType } from 'util/siteManagementTypes';

export const fetchEdiCategories = async (
  successCallback?: (data: EdiCategoryType) => void
) => {
  const api = createAxios();
  const { data } = await api.get<EdiCategoryType>(
    API_ENDPOINTS.COMMONS_EDI_CATEGORIES
  );
  successCallback?.(data);
};

export const getStations = async (params?: {
  successCallback?: (data: Station[]) => void;
  hideLoading?: boolean;
}) => {
  const { successCallback, hideLoading } = params ?? {};
  const api = createAxios({
    notShowLoading: hideLoading,
  });

  try {
    const { data } = await api.get<Station[]>(API_ENDPOINTS.COMMONS_STATIONS);
    data && successCallback?.(data);
  } catch (error) {
    handleError(error);
  }
};

export const getOperatorPools = async (
  successCallback?: (data: { id: number }[]) => void
) => {
  const api = createAxios();
  try {
    const { data } = await api.get<{ id: number }[]>(
      API_ENDPOINTS.COMMONS_OPERATOR_POOLS
    );
    data && successCallback?.(data);
  } catch (error) {
    handleError(error);
  }
};

export const getSiteCollects = async (params?: {
  successCallback?: (data: OptionItem[]) => void;
  hideLoading?: boolean;
}) => {
  const { successCallback, hideLoading } = params ?? {};
  const api = createAxios({
    notShowLoading: hideLoading,
  });

  try {
    const { data } = await api.get<OptionItem[]>(
      API_ENDPOINTS.COMMONS_SITE_COLLECTS
    );
    data && successCallback?.(data);
  } catch (error) {
    handleError(error);
  }
};

export const getMaterialNames = async (params?: {
  isEmissionAll?: boolean;
  successCallback?: (
    data: { materialName: string; englishMaterialName: string }[]
  ) => void;
  hideLoading?: boolean;
}) => {
  const { successCallback, hideLoading, ...apiParams } = params ?? {};
  const api = createAxios({
    notShowLoading: hideLoading,
  });

  try {
    const { data } = await api.get<
      { materialName: string; englishMaterialName: string }[]
    >(API_ENDPOINTS.COMMONS_MATERIAL_NAMES, {
      params: apiParams,
    });
    data && successCallback?.(data);
  } catch (error) {
    handleError(error);
  }
};

export const getRecycleMethods = async (params?: {
  successCallback?: (data: (OptionItem & { englishName: string })[]) => void;
  hideLoading?: boolean;
}) => {
  const { successCallback, hideLoading } = params ?? {};
  const api = createAxios({
    notShowLoading: hideLoading,
  });

  try {
    const { data } = await api.get<(OptionItem & { englishName: string })[]>(
      API_ENDPOINTS.COMMONS_RECYCLE_METHODS
    );
    data && successCallback?.(data);
  } catch (error) {
    handleError(error);
  }
};

export const getCommonWasteUnits = async (params?: {
  successCallback?: (data: { id: number; name: string }[]) => void;
}) => {
  const { successCallback } = params ?? {};

  try {
    const { data } = await createAxios().get<{ id: number; name: string }[]>(
      API_ENDPOINTS.COMMONS_WASTE_UNITS
    );

    if (data) {
      successCallback?.(data);
    }
  } catch (error) {
    handleError(error);
  }
};

export const getCommonWasteUnitNames = async (params?: {
  getAll?: boolean;
  successCallback?: (data: { name: string }[]) => void;
}) => {
  const { successCallback, getAll } = params ?? {};

  try {
    const { data } = await createAxios().get<{ name: string }[]>(
      API_ENDPOINTS.COMMONS_WASTE_UNIT_NAMES,
      {
        params: {
          type: getAll ? 'all' : undefined,
        },
      }
    );

    if (data) {
      successCallback?.(data);
    }
  } catch (error) {
    handleError(error);
  }
};

export const getCommonTenants = async (params?: {
  successCallback?: (data: TenantBase[]) => void;
}) => {
  const { successCallback } = params ?? {};

  try {
    const { data } = await createAxios().get<TenantBase[]>(
      API_ENDPOINTS.COMMONS_TENANTS
    );

    if (data) {
      successCallback?.(data);
    }
  } catch (error) {
    handleError(error);
  }
};

export const getCommonSites = async (params?: {
  successCallback?: (data: { id: number; name: string }[]) => void;
}) => {
  const { successCallback } = params ?? {};

  try {
    const { data } = await createAxios().get<{ id: number; name: string }[]>(
      API_ENDPOINTS.COMMONS_SITES
    );

    if (data) {
      successCallback?.(data);
    }
  } catch (error) {
    handleError(error);
  }
};

export const getScalesModels = async (params?: {
  successCallback?: (data: ScalesModelDetail[]) => void;
}) => {
  const { successCallback } = params ?? {};

  try {
    const { data } = await createAxios().get<ScalesModelDetail[]>(
      API_ENDPOINTS.COMMONS_SCALES_MODELS
    );

    if (data) {
      successCallback?.(data);
    }
  } catch (error) {
    handleError(error);
  }
};

/* eslint-disable @typescript-eslint/no-explicit-any */
export const getCommonCollectRoutes = async (params?: {
  successCallback?: (data: Pick<CollectRoute, 'id' | 'routeName'>[]) => void;
  failedCallback?: (error: any) => void;
}) => {
  const { successCallback, failedCallback } = params ?? {};

  try {
    const { data } = await createAxios().get<
      Pick<CollectRoute, 'id' | 'routeName'>[]
    >(API_ENDPOINTS.COMMONS_COLLECT_ROUTES);

    if (data) {
      successCallback?.(data);
    }
  } catch (error) {
    handleError(error);
    failedCallback?.(error);
  }
};
